import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { addToast, useModals } from 'core/notifications';
import i18n from 'i18next';

import { Loading } from 'uikit/loaders';
import { Markdown, Typo } from 'uikit/content';

import useBulkImport from '../hooks/useBulkImport';

import BulkImportModalForm from './bulkImportModalForm';
import BulkImportModalTable from './bulkImportModalTable';

import styles from '../styles/bulkImportModal.module.scss';

const BulkImportModal = ({
  getEnqueueAxiosConfig = () => {},
  getPreviewAxiosConfig = () => {},
  initialData = {},
  settingsAxiosConfig = {},
  typeKey = '',
}) => {
  const {
    dataErrors,
    enqueueImport,
    fetchPreview,
    isLoading,
    isSubmitting,
    model,
    setModel,
    settings,
  } = useBulkImport({
    getEnqueueAxiosConfig,
    getPreviewAxiosConfig,
    initialData,
    settingsAxiosConfig,
  });

  const typeStrings = useMemo(() => {
    const getTypeString = ({ isLowerCase, isPlural } = {}) => {
      const string = i18n.t(typeKey, { count: Number(!isPlural) });

      return isLowerCase ? string.toLowerCase() : string;
    };

    return {
      type: getTypeString(),
      typeLowerCase: getTypeString({ isLowerCase: true }),
      typePlural: getTypeString({ isPlural: true }),
      typePluralLowerCase: getTypeString({ isLowerCase: true, isPlural: true }),
    };
  }, [typeKey]);

  const preview = model.csv_preview;
  const hasPreview = Boolean(preview);
  const { removeModal } = useModals();

  const handleSubmit = async () => {
    if (!hasPreview) return fetchPreview();

    const hasEnqueued = await enqueueImport();

    if (!hasEnqueued) return;

    removeModal();
    addToast(i18n.t('upload-accepted'), 'success');
  };

  return (
    <>
      <Typo className={styles.title} element="h3" variant="d05">
        {i18n.t(
          hasPreview ? 'bulk-import-of-type-preview-title' : 'bulk-import-of-type-title',
          typeStrings,
        )}
      </Typo>
      {isLoading ? (
        <Loading className={styles.loader} />
      ) : (
        <div className={styles.content}>
          {!hasPreview && (
            <Markdown
              className={styles.body}
              content={i18n.t('bulk-import-of-type-body', {
                columnDescriptionsFileUrl: settings.column_descriptions_file_url,
                sampleFileUrl: settings.sample_file_url,
                ...typeStrings,
              })}
            />
          )}
          <BulkImportModalTable
            data={preview || settings.column_descriptions}
            isTemplate={!hasPreview}
          />
          <BulkImportModalForm
            dataErrors={dataErrors}
            hasPreview={hasPreview}
            isSubmitting={isSubmitting}
            model={model}
            onSubmit={handleSubmit}
            onUpdate={setModel}
          />
        </div>
      )}
    </>
  );
};

BulkImportModal.propTypes = {
  getEnqueueAxiosConfig: PropTypes.func,
  getPreviewAxiosConfig: PropTypes.func,
  initialData: PropTypes.shape({}),
  settingsAxiosConfig: PropTypes.shape({}),
  typeKey: PropTypes.string,
};

export default React.memo(BulkImportModal);
